import {
  AdvancedSettings,
  AvoPageName,
  BacktestingStatus,
  DCAStrategyData,
  DefaultOrder,
  Direction,
  LongShort,
  NumberOption,
  OrderBaseline,
  OverrideFieldType,
  StopType,
  StrategyData,
  StrategyStyleType,
  UnifiedStrategyData,
} from "@shared/types";
import {
  BacktestingPnlStatus,
  BaseBotFormValues,
  BotFormSetters,
  BotFormValues,
  EntryGraceBaseline,
  LeverageType,
  OverlapData,
  ParsedDcaStrategyData,
  RestoreChangesType,
  Steps,
  StrategyBaseline,
  StrategyTabs,
  TargetsTabs,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { invert } from "lodash";
import { STOP_TYPE_TO_DISPLAY } from "@shared/common/constants";
import { Color } from "@shared/common/Themes/types";

export const MAX_PRICE = 1000000;
export const MAX_NUM_GRIDS = 75;
export const MIN_NUM_GRIDS = 3;
export const MAX_SECONDS_COOLDOWN = 1000000;
export const GENERAL_MAX_VALUE = 100000;
export const NO_LIMIT_MAX_VALUE = 999999999999;
export const MAX_DEFAULT_TAKE_PROFIT_PERCENT = 100;
export const MAX_BOT_NAME_LENGTH = 32;
export const MAX_BACKTEST_NAME_LENGTH = 48;
export const MAX_BACKEND_BOT_NAME_LENGTH = 64;
export const MAX_FETCH_ALL_ACCOUNT_AMOUNTS = 5;

export const LONG_SHORT_OPTIONS = [
  { name: Direction.LONG, value: LongShort.LONG },
  { name: Direction.SHORT, value: LongShort.SHORT },
];

export const STOP_TYPE_OPTIONS = [
  { name: STOP_TYPE_TO_DISPLAY[StopType.LIMIT], value: StopType.LIMIT },
  { name: STOP_TYPE_TO_DISPLAY[StopType.MARKET], value: StopType.MARKET },
];

export const BOT_FORM_TABS_ORDER = [
  Steps.GENERAL,
  Steps.ENTRIES,
  Steps.TAKE_PROFITS,
  Steps.STOP,
  Steps.ADVANCED,
];

export const SINGLE_COLUMN_MAX_WIDTH = 420;
export const PAGE_PADDING = 14;

export const AVO_FIELD_NAMES = {
  [OrderTypes.ENTRY]: "Entry Orders Summary",
  [OrderTypes.TAKE_PROFIT]: "Take-Profit Orders Summary",
  [OrderTypes.STOP]: "Stop Orders Summary",
};

export const DCA_BOT_HELP_DESCRIPTIONS = {
  [OrderTypes.ENTRY]:
    "This is an estimated preview of your bot’s entry orders based on your defined strategy. Actual execution prices may differ (based on the first market order and whether trailing is enabled)",
  [OrderTypes.TAKE_PROFIT]:
    "This is an estimated preview of your bot’s take-profit target based on your defined strategy and the distance is relative only to the first order price. The actual execution price may differ.",
  [OrderTypes.STOP]:
    "This is an estimate of your stop-loss order. The actual execution price may differ.",
};

export const TRADING_VIEW_BOT_HELP_DESCRIPTIONS = {
  [OrderTypes.ENTRY]:
    "This is an estimated preview of your bot’s entry orders distance from the trigger price based on your defined strategy.",
  [OrderTypes.TAKE_PROFIT]:
    "This is an estimated preview of your bot’s take-profit targets based on your defined strategy. The actual execution price may differ.",
  [OrderTypes.STOP]:
    "This is an estimate of your stop-loss order. The actual execution price may differ.",
};

export const channelValue = Symbol.for("channel_value");
export const multiValue = Symbol.for("multi");
export const multiToggleValue = Symbol.for("multi_toggle");
export const empty = "empty";
export const off = "off";

export const DEFAULT_TAKE_PROFIT = {
  percent: "1",
  baseline: OrderBaseline.AVERAGE_ENTRIES,
};
export const DEFAULT_SIGNAL_BOT_TAKE_PROFIT: DefaultOrder<string> = {
  percent: channelValue,
  baseline: OrderBaseline.AVERAGE_ENTRIES,
};
export const DEFAULT_STOP = {
  percent: "5",
  baseline: OrderBaseline.AVERAGE_ENTRIES,
};
export const DEFAULT_GRID_BOT_STOP = {
  percent: "5",
  baseline: OrderBaseline.LAST_ENTRY,
};
export const DEFAULT_STRATEGY_TARGETS = [25, 25, 25, 25];

export const RESET_TAB_BUTTON_HEIGHT = 28;

export const FIELD_TO_PARENT_FIELD: { [key in ToggleField]?: ToggleField } = {
  [ToggleField.FIRST_ENTRY]: ToggleField.ENTRIES,
  [ToggleField.TRAILING_ENTRY]: ToggleField.ENTRIES,
  [ToggleField.ENTRY_GRACE_PERCENT]: ToggleField.ENTRIES,
  [ToggleField.TAKE_PROFIT_STRATEGY]: ToggleField.TAKE_PROFITS,
  [ToggleField.TRAILING_TAKE_PROFIT]: ToggleField.TAKE_PROFITS,
  [ToggleField.TAKE_PROFIT_GRACE_PERCENT]: ToggleField.TAKE_PROFITS,
  [ToggleField.STOP_TIMEOUT]: ToggleField.STOP,
  [ToggleField.TRAILING_STOP]: ToggleField.STOP,
};

export const FORM_KEY_TO_TOGGLE_FIELD: {
  [key in keyof BaseBotFormValues]?: ToggleField;
} = {
  entryStrategy: ToggleField.ENTRIES,
  trailingEntry: ToggleField.TRAILING_ENTRY,
  entryGracePercent: ToggleField.ENTRY_GRACE_PERCENT,
  defaultTakeProfit: ToggleField.TAKE_PROFITS,
  takeProfitStrategy: ToggleField.TAKE_PROFIT_STRATEGY,
  trailingTakeProfit: ToggleField.TRAILING_TAKE_PROFIT,
  defaultStop: ToggleField.STOP,
  stopTimeout: ToggleField.STOP_TIMEOUT,
  trailingStopData: ToggleField.TRAILING_STOP,
  cancelOnProfitBeforeEntry: ToggleField.CLOSE_ON_TP_BEFORE_ENTRY,
  skipWithoutStopLoss: ToggleField.SKIP_WITHOUT_STOP_LOSS,
  takeProfitGracePercent: ToggleField.TAKE_PROFIT_GRACE_PERCENT,
  operationHoursEvents: ToggleField.OPERATION_HOURS,
};

export const FORM_KEY_TO_OVERRIDE_FIELD: {
  [key in keyof BaseBotFormValues]?: OverrideFieldType;
} = {
  amountValue: OverrideFieldType.AMOUNT_PER_TRADE,
  cancelOnProfitBeforeEntry: OverrideFieldType.CANCEL_ON_PROFIT_BEFORE_ENTRY,
  defaultStop: OverrideFieldType.DEFAULT_STOP,
  defaultTakeProfit: OverrideFieldType.DEFAULT_TAKE_PROFIT,
  entryStrategy: OverrideFieldType.ENTRY_STRATEGY,
  takeProfitStrategy: OverrideFieldType.TAKE_PROFIT_STRATEGY,
  trailingEntry: OverrideFieldType.TRAILING_ENTRY,
  trailingStopData: OverrideFieldType.TRAILING_STOP,
  trailingTakeProfit: OverrideFieldType.TRAILING_TAKE_PROFIT,
  multiSymbolMarginData: OverrideFieldType.LEVERAGE,
};

export const TOGGLE_FIELD_TO_FORM_KEY = invert(FORM_KEY_TO_TOGGLE_FIELD) as {
  [key in ToggleField]?: keyof BaseBotFormValues;
};

export const BOT_FORM_KEY_TO_SETTER_NAME: Record<
  keyof BotFormValues,
  keyof BotFormSetters
> = {
  name: "setName",
  subGroup: "setSubGroup",
  account: "setAccount",
  accounts: "setAccounts",
  symbols: "setSymbols",
  includeNewSymbols: "setIncludeNewSymbols",
  subGroupBotConfiguration: "setSubGroupBotConfiguration",
  trailingStopData: "setTrailingStopData",
  entryStrategy: "setEntryStrategy",
  takeProfitStrategy: "setTakeProfitStrategy",
  defaultTakeProfit: "setDefaultTakeProfit",
  defaultStop: "setDefaultStop",
  amountType: "setAmountType",
  amountValue: "setAmountValue",
  amountPercentageBaseType: "setAmountPercentageBaseType",
  amountCustomBaseValue: "setAmountCustomBaseValue",
  longShort: "setLongShort",
  marginType: "setMarginType",
  multiSymbolMarginData: "setMultiSymbolMarginData",
  multiplier: "setMultiplier",
  trailingEntry: "setTrailingEntry",
  trailingTakeProfit: "setTrailingTakeProfit",
  stopTimeout: "setStopTimeout",
  advancedSettings: "setAdvancedSettings",
  stopType: "setStopType",
  stopLimitPriceReduction: "setStopLimitPriceReduction",
  cancelOnProfitBeforeEntry: "setCancelOnProfitBeforeEntry",
  entryGracePercent: "setEntryGracePercent",
  entryGraceBaseline: "setEntryGraceBaseline",
  numberOfContinuousStopsTillBotStop: "setNumberOfContinuousStopsTillBotStop",
  gridsData: "setGridsData",
  initialized: "setInitialized",
  checkedFields: "setCheckedFields",
  overrideFields: "setOverrideFields",
  isLeverageExchanges: "setIsLeverageExchanges",
  skipWithoutStopLoss: "setSkipWithoutStopLoss",
  alternativeUsdPairs: "setAlternativeUsdPairs",
  takeProfitGracePercent: "setTakeProfitGracePercent",
  operationHoursEvents: "setOperationHoursEvents",
};

export const MULTI_VALUE_TOGGLE_BOT_KEYS: Set<
  keyof BaseBotFormValues | keyof DCAStrategyData
> = new Set([
  "trailingStopData",
  "entryStrategy",
  "takeProfitStrategy",
  "defaultTakeProfit",
  "defaultStop",
  "trailingEntry",
  "trailingTakeProfit",
  "stopTimeout",
  "first_order_percentage",
]);

export const STOP_TIMEOUT_TITLE = "Stop-Loss Timeout";
export const STOP_BASELINE_TITLE = "Stop-Loss Baseline";
export const STOP_PERCENT_TITLE = "Stop-Loss Distance Percent";
export const TAKE_PROFIT_BASELINE_TITLE = "Take-Profit Baseline";
export const TAKE_PROFIT_PERCENT_TITLE = "Take-Profit Distance Percent";

export const DCA_STRATEGY_KEY_TO_TITLE: {
  [key in keyof ParsedDcaStrategyData]: string;
} = {
  baseline: "Baseline",
  numOrders: "Number of Orders",
  numActiveOrders: "Number of Active Orders",
  firstOrderPercent: "First Entry Order Amount Percent",
  amountScale: "Amount Scale",
  priceDifference: "Price Difference",
  priceScale: "Price Scale",
};
export const MAX_PRICE_DIFFERENCE_TEXT = "Max orders price difference";
export const GRID_MAX_PRICE_DIFFERENCE_TEXT = "Max grids price difference";

export const ALL_OVERRIDES_TOGGLE_FIELDS = new Set([
  ToggleField.TRAILING_TAKE_PROFIT,
  ToggleField.TRAILING_ENTRY,
  ToggleField.STOP,
  ToggleField.TRAILING_STOP,
  ToggleField.CLOSE_ON_TP_BEFORE_ENTRY,
]);
export const WITHOUT_OFF_TOGGLE_FIELDS = new Set([
  ToggleField.ENTRIES,
  ToggleField.ENTRY_RATIOS,
  ToggleField.ENTRY_PRICE_STRATEGY,
  ToggleField.ENTRY_NUM_ORDERS,
  ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
  ToggleField.TAKE_PROFITS,
  ToggleField.TAKE_PROFIT_STRATEGY,
  ToggleField.TAKE_PROFIT_RATIOS,
  ToggleField.TAKE_PROFIT_PRICE_STRATEGY,
  ToggleField.TAKE_PROFIT_NUM_ORDERS,
  ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS,
  ToggleField.MARGIN,
]);
export const WITHOUT_CHANNEL_TOGGLE_FIELDS = new Set([
  ToggleField.ENTRY_GRACE_PERCENT,
  ToggleField.STOP_TIMEOUT,
  ToggleField.TAKE_PROFIT_DYNAMIC,
  ToggleField.SKIP_WITHOUT_STOP_LOSS,
  ToggleField.ALTERNATIVE_USD_PAIRS,
  ToggleField.TAKE_PROFIT_GRACE_PERCENT,
  ToggleField.OPERATION_HOURS,
]);

export const TOGGLE_FIELD_TO_OVERRIDE_KEY: {
  [key in ToggleField]?: OverrideFieldType;
} = {
  [ToggleField.TRAILING_ENTRY]: OverrideFieldType.TRAILING_ENTRY,
  [ToggleField.TRAILING_TAKE_PROFIT]: OverrideFieldType.TRAILING_TAKE_PROFIT,
  [ToggleField.TRAILING_STOP]: OverrideFieldType.TRAILING_STOP,
  [ToggleField.TAKE_PROFITS]: OverrideFieldType.DEFAULT_TAKE_PROFIT,
  [ToggleField.STOP]: OverrideFieldType.DEFAULT_STOP,
  [ToggleField.ENTRIES]: OverrideFieldType.ENTRY_STRATEGY,
  [ToggleField.ENTRY_RATIOS]: OverrideFieldType.ENTRY_STRATEGY,
  [ToggleField.TAKE_PROFIT_RATIOS]: OverrideFieldType.TAKE_PROFIT_STRATEGY,
  [ToggleField.TAKE_PROFIT_STRATEGY]: OverrideFieldType.TAKE_PROFIT_STRATEGY,
  [ToggleField.MARGIN]: OverrideFieldType.LEVERAGE,
  [ToggleField.CLOSE_ON_TP_BEFORE_ENTRY]:
    OverrideFieldType.CANCEL_ON_PROFIT_BEFORE_ENTRY,
};

export const ADVANCED_KEY_TO_INPUT_DISPLAY_NAME: Record<
  keyof AdvancedSettings,
  string
> = {
  number_of_trades_till_bot_stop: "Number of Trades Until Bot Stop",
  cooldown_between_opening_trades: "Cooldown Between Trades",
  cooldown_after_trade_close: "Cooldown After Trade Close",
  number_of_simultaneous_trades: "Number of Simultaneous Trades",
  cooldown_between_opening_trades_per_symbol:
    "Cooldown Between Trades per Symbol",
  min_symbol_price: "Min Symbol Price (USD)",
  min_symbol_volume: "Min Symbol 24H Volume (USD)",
  concurrent_max_usd_invested: "Max Concurrent Amount (USD)",
};

export const CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE =
  "Close Trade On TP Before Entry";
export const DYNAMIC_TAKE_PROFITS_TITLE = "Moving Take-Profits";
export const PRICE_ADJUSTMENT_BASELINE_TITLE = "Price Adjustment Baseline";
export const STOP_LIMIT_PRICE_REDUCTION_TITLE = "Stop Limit Price Reduction";
export const SKIP_SIGNALS_WITHOUT_STOP_TITLE = "Skip Signals Without Stop-Loss";
export const ALTERNATIVE_USD_PAIRS_TITLE = "Alternative USD Pairs";
export const STOP_TYPE_TITLE = "Stop Type";
export const INCLUDE_NEW_SYMBOLS_TITLE = "Include newly listed symbols";
export const TAKE_PROFIT_GRACE_TITLE = "Take-Profit Grace";
export const OPERATION_HOURS_TITLE = "Operation hours";

export const RESTORE_CHANGES_TYPE_TO_DISPLAY = {
  [RestoreChangesType.ORIGINAL_BOT]: "Restore Previous Values",
  [RestoreChangesType.TEMPLATE]: "Restore Template Values",
};

export const STRATEGY_BASELINE_TO_DISPLAY = {
  [StrategyBaseline.PREVIOUS_ORDER]: "Previous Grid",
  [StrategyBaseline.FIRST_ORDER]: "First Grid",
};

export const ORDER_BASELINE_TO_DISPLAY: { [key in OrderBaseline]?: string } = {
  [OrderBaseline.FIRST_ENTRY]: "First Entry",
  [OrderBaseline.AVERAGE_ENTRIES]: "Average Entries",
  [OrderBaseline.LAST_ENTRY]: "Last Entry",
};

export const DEFAULT_GRID_BOT_STRATEGY: DCAStrategyData = {
  first_order_percentage: undefined,
  strategy_style: StrategyStyleType.DCA,
  amount_scale: 1,
  price_scale: 1,
  price_difference: 0.6,
  number_of_orders: 50,
  number_of_active_orders: 3,
  baseline: StrategyBaseline.FIRST_ORDER,
};

export const DEFAULT_GRID_PRICE_RANGE_RATIO = 0.3;
export const DEFAULT_AMOUNT_PERCENT = 10;
export const DEFAULT_STOP_LIMIT_PRICE_REDUCTION = 2;
export const MIN_CONSECUTIVE_GRIDS_DISTANCE = 0.2;
export const INVALID_PRICES_ERROR_MESSAGE =
  "Some of the prices below are invalid";
export const INVALID_GRIDS_DISTANCE_ERROR_MESSAGE = `The minimum distance between consecutive grids should be at least ${MIN_CONSECUTIVE_GRIDS_DISTANCE}%.`;

export const TV_BOT_ORDERS_SORTER_HEIGHT = 56;

export const OVERLAP_KEY_TO_DISPLAY: Record<keyof OverlapData, string> = {
  symbols: "symbols",
  stopType: "stop type",
  longShort: "direction",
  marginType: "margin type",
  multiplier: "multiplier",
};

export const STRATEGY_TYPE_MAPPING = {
  [StrategyStyleType.CUSTOM]: StrategyTabs.CUSTOM,
  [StrategyStyleType.DCA]: StrategyTabs.DCA,
  [StrategyStyleType.BUILT_IN]: StrategyTabs.BUILT_IN,
};

export const ADVANCED_BOT_FORM_KEYS: (keyof BaseBotFormValues)[] = [
  "trailingEntry",
  "entryGracePercent",
  "trailingTakeProfit",
  "stopTimeout",
  "cancelOnProfitBeforeEntry",
  "multiSymbolMarginData",
];

export const ADVANCED_SETTINGS_KEYS: (keyof AdvancedSettings)[] = [
  "number_of_trades_till_bot_stop",
  "cooldown_between_opening_trades",
  "cooldown_after_trade_close",
  "number_of_simultaneous_trades",
  "cooldown_between_opening_trades_per_symbol",
  "min_symbol_price",
  "min_symbol_volume",
  "concurrent_max_usd_invested",
];

export const STRATEGY_KEYS: (keyof ParsedDcaStrategyData | "targets")[] = [
  "numOrders",
  "numActiveOrders",
  "amountScale",
  "priceDifference",
  "priceScale",
  "firstOrderPercent",
  "baseline",
  "targets",
];

export const DEFAULT_ADVANCED_SETTINGS_VALUE = {
  number_of_trades_till_bot_stop: undefined,
  cooldown_between_opening_trades: undefined,
  cooldown_after_trade_close: undefined,
  number_of_simultaneous_trades: undefined,
  cooldown_between_opening_trades_per_symbol: undefined,
  min_symbol_price: undefined,
  min_symbol_volume: undefined,
  concurrent_max_usd_invested: undefined,
};

export const LEVERAGE_TYPE_OPTIONS: NumberOption[] = [
  { value: LeverageType.EXACT, name: "Exactly" },
  { value: LeverageType.UP_TO, name: "Up to" },
];

export const DEFAULT_SIGNALS_BOT_STRATEGY: StrategyData = {
  strategy_style: channelValue,
  baseline: channelValue,
  builtin_strategy_type: channelValue,
  amount_scale: channelValue,
  price_scale: channelValue,
  price_difference: channelValue,
  number_of_orders: channelValue,
  number_of_active_orders: channelValue,
};

export const DEFAULT_SIGNALS_CONFIGURATION_STRATEGY: StrategyData = {
  strategy_style: channelValue,
  baseline: undefined,
  builtin_strategy_type: undefined,
  amount_scale: undefined,
  price_scale: undefined,
  price_difference: undefined,
  number_of_orders: undefined,
  number_of_active_orders: undefined,
};

export const ENTRY_STRATEGY_TOGGLE_FIELDS = new Set([
  ToggleField.ENTRY_RATIOS,
  ToggleField.ENTRY_PRICE_STRATEGY,
  ToggleField.ENTRY_NUM_ORDERS,
  ToggleField.ENTRY_NUM_ACTIVE_ORDERS,
]);

export const TOGGLE_FIELD_TO_STRATEGY_FIELDS: {
  [key in ToggleField]?: (keyof UnifiedStrategyData)[];
} = {
  [ToggleField.ENTRY_RATIOS]: [
    "strategy_style",
    "first_order_percentage",
    "amount_scale",
    "builtin_strategy_type",
    "targets",
  ],
  [ToggleField.TAKE_PROFIT_RATIOS]: [
    "strategy_style",
    "first_order_percentage",
    "amount_scale",
    "builtin_strategy_type",
    "targets",
  ],
  [ToggleField.ENTRY_PRICE_STRATEGY]: ["price_scale", "price_difference"],
  [ToggleField.TAKE_PROFIT_PRICE_STRATEGY]: ["price_scale", "price_difference"],
  [ToggleField.ENTRY_NUM_ORDERS]: ["number_of_orders"],
  [ToggleField.TAKE_PROFIT_NUM_ORDERS]: ["number_of_orders"],
  [ToggleField.ENTRY_NUM_ACTIVE_ORDERS]: ["number_of_active_orders"],
  [ToggleField.TAKE_PROFIT_NUM_ACTIVE_ORDERS]: ["number_of_active_orders"],
  [ToggleField.FIRST_ENTRY]: ["first_order_percentage"],
};

export const CONFIRM_AUTO_TRADING_MODAL_CONTENT = [
  "You already have auto-trading enabled for this group.",
  "Activating this bot without disabling auto-trading first might result in duplicate trades being created.",
  "Are you sure you wish to continue?",
];
export const CONFIRM_BULK_AUTO_TRADING_MODAL_CONTENT = [
  "You already have auto-trading enabled for some of the selected groups.",
  "Activating these bots without disabling auto-trading first might result in duplicate trades being created.",
  "Are you sure you wish to continue?",
];
export const CONFIRM_BOT_AUTO_TRADING_MODAL_CONTENT = [
  "You already have an active signals bot connected to this group.",
  "Activating auto-trading without disabling the signals bot first might result in duplicate trades being created.",
  "Are you sure you wish to continue?",
];
export const LEVERAGED_TRAILING_MESSAGE =
  "For leveraged signals, the selected value will be divided by the leverage value.";
export const BOT_MARGIN_DATA_MODIFIED_MESSAGE =
  "The multiplier for some of the symbols is different than the original bot.";
export const BOT_MAX_LEVERAGE_WARNING_MESSAGE =
  "The multiplier for some of the symbols was adjusted to the maximum that is allowed on the exchange.";
export const SIGNALS_BOT_OVERRIDE_TEXT = "Only use if not defined by group";

export const DEFAULT_OVERRIDE_FIELDS = new Set([
  OverrideFieldType.AMOUNT_PER_TRADE,
]);
export const DEFAULT_GROUP_CONFIGURATION_TITLE = "Default Group Configuration";
export const DEFAULT_GROUP_CONFIGURATION_DESCRIPTION =
  "The group admin hasn't set a custom group configuration so the default configuration is in use. Unless overridden by your personal bot configuration, the entries and take-profit targets will have evenly distributed amount ratios, and advanced features like trailing will be disabled.";

export const BACKTESTING_STATUS_TO_COLOR = {
  [BacktestingStatus.IN_PROGRESS]: "orange",
  [BacktestingStatus.COMPLETED]: "green",
  [BacktestingStatus.FAILED]: "red",
};

export const BACKTESTING_STATUS_TO_DISPLAY_NAME: {
  [key in BacktestingStatus]?: string;
} = {
  [BacktestingStatus.IN_PROGRESS]: "In progress",
  [BacktestingStatus.COMPLETED]: "Completed",
  [BacktestingStatus.FAILED]: "Failed",
};

export const BACKTESTING_PNL_STATUS_TO_COLOR: Record<
  BacktestingPnlStatus,
  Color
> = {
  [BacktestingPnlStatus.WINNING]: "green",
  [BacktestingPnlStatus.LOSING]: "red",
  [BacktestingPnlStatus.NEUTRAL]: "yellow",
};

export const TARGETS_STATISTIC_TABS_TO_DISPLAY = {
  [TargetsTabs.ENTRIES]: "Entries",
  [TargetsTabs.TAKE_PROFITS]: "Take-Profits",
};

export const TARGETS_STATISTIC_TABS_TO_STROKE_COLOR = {
  [TargetsTabs.ENTRIES]: { "0%": "#B8F0BB", "100%": "#0AC032" },
  [TargetsTabs.TAKE_PROFITS]: { "0%": "#FFB8B4", "100%": "#DB3328" },
};

export const BACKTESTING_IN_PROGRESS_MESSAGE =
  "The backtesting is still in progress. The statistics shown below represent partial data and will be updated as the backtesting continues.";
export const FAILED_BACKTESTING_EXTRA_DETAILS_MESSAGE =
  "You can't view additional details for failed backtests.";

export const BACKTESTING_DISCLAIMER =
  "Disclaimer: Past results do not guarantee future outcomes.";
export const BACKTESTING_DISCLAIMER_ARTICLE = "9572268";
export const BACKTESTING_VALUES_MODIFIED_MESSAGE =
  "Some of the fields have been modified and do not match the applied backtesting configuration.";

export const BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS = {
  description:
    "The maximum observed loss from a portfolio's peak to its lowest point, before a new peak is reached.",
  avoPageName: AvoPageName.BACKTESTING,
  avoFieldName: "Max Drawdown",
  articleId: "9575281",
};

export const AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS = {
  description:
    "Considers both open and closed trades. For open trades, the duration is measured until the end of the selected timeframe.",
  avoPageName: AvoPageName.BACKTESTING,
  avoFieldName: "Avg Trade Duration",
  articleId: "9575281",
};

export const MAX_CHANNEL_CONFIGURATION_PERCENT = 20;

export const ENTRY_GRACE_BASELINE_TO_DISPLAY_TEXT = {
  [EntryGraceBaseline.PRICE_REACHED]: "Entry Price Reached",
  [EntryGraceBaseline.IMMEDIATE]: "Immediately",
};

export const MARGIN_GLOBAL_SETTINGS_KEY = "FALLBACK_SYMBOL";
export const SHOW_MODIFIED_SYMBOLS_TITLE = "Show Custom Symbols";
